import { makeAutoObservable } from 'mobx';
import { inject, injectable } from 'inversify';
import { IAddServiceButtonFeatureViewModel } from '@shared/types/mobx/features/osiAccruals';
import logger from 'js-logger';
import { notifications } from '@shared/utils/notifications';
import { AccuralMethod, OsiServiceResponse } from '@shared/types/osi/services';
import { MutableRefObject } from 'react';
import { RefHandle } from '@features/osi/accruals/abonents/ui/OsiServiceAbonentsButton';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import { queryClient } from '@shared/api/reactQuery';
import { getPostApiOsiServicesMutationOptions } from '@shared/api/orval/osi-services/osi-services';
import { type RequestsOsiServiceRequest } from '@shared/api/orval/models/requestsOsiServiceRequest';
import { ApiResponseDbOsiServiceOSICoreTypes } from '@shared/api/orval/models/apiResponseDbOsiServiceOSICoreTypes';

@injectable()
export class AddServiceButtonFeatureViewModel implements IAddServiceButtonFeatureViewModel {
  isDialogOpen = false;

  hookForm: any = null;

  isLoading = false;

  // Force update confirmation dialog
  isForceUpdateConfirmDialogOpen = false;

  forceUpdatePayload: RequestsOsiServiceRequest | null = null;

  forceUpdateMessage: string | null = null;

  constructor(@inject(OsiServiceIdentifier) private osiModule: OsiService) {
    makeAutoObservable(this);
  }

  _service: OsiServiceResponse | null = null;

  get service(): OsiServiceResponse | null {
    return this._service;
  }

  set service(service: OsiServiceResponse) {
    this._service = service;
  }

  _abonentsTableRef: MutableRefObject<RefHandle | null> | null = null;

  set abonentsTableRef(ref: any) {
    this._abonentsTableRef = ref;
  }

  reloadCb: any = () => {};

  // OSI-805: список методов начисления текущей группы (тот же, что рендерится в выпадающем списке
  // «Метод начислений» формы). Прокидывается из AddServiceButton через setAccrualMethods —
  // тем же приёмом, что уже используется для hookForm/reloadCb/abonentsTableRef.
  // Нужен, чтобы в createService сопоставить выбранный accrualMethodId с его code (см. комментарий там).
  private _accrualMethods: AccuralMethod[] = [];

  setAccrualMethods = (accrualMethods: AccuralMethod[]) => {
    this._accrualMethods = accrualMethods ?? [];
  };

  closeDialog = () => {
    this.isDialogOpen = false;
    this.hookForm?.reset();
    this.closeForceUpdateConfirmDialog();
  };

  closeForceUpdateConfirmDialog = (): void => {
    this.isForceUpdateConfirmDialogOpen = false;
    this.forceUpdatePayload = null;
    this.forceUpdateMessage = null;
  };

  confirmForceUpdate = async (): Promise<void> => {
    if (!this.forceUpdatePayload) return;

    this.isLoading = true;

    try {
      const mutation = queryClient.getMutationCache().build(
        queryClient,
        getPostApiOsiServicesMutationOptions({
          mutation: {
            onMutate: () => {
              this.isLoading = true;
            },
            onSettled: () => {
              this.isLoading = false;
            }
          }
        })
      );

      const response = await mutation.execute({
        data: this.forceUpdatePayload
      });

      // Check response code after retry
      if (response && response.code !== 0) {
        notifications.error(response.message ?? '');
        return;
      }

      this.reloadCb?.();
      this.closeDialog();
      this.closeForceUpdateConfirmDialog();
    } catch (e) {
      if (e instanceof Error) {
        notifications.error();
        return;
      }
      notifications.error(e);
    } finally {
      this.isLoading = false;
    }
  };

  openDialog = () => {
    this.isDialogOpen = true;
    this.hookForm?.reset();
    this.hookForm?.trigger();
  };

  setReloadCb = (cb: any) => {
    this.reloadCb = cb;
  };

  createService = async (values: any) => {
    if (!this.osiModule.osiId && this.osiModule.osiId !== 0) return;

    this.isLoading = true;

    try {
      const payload: RequestsOsiServiceRequest = {
        osiId: this.osiModule.osiId,
        nameRu: values.service.nameRu,
        nameKz: values.service.nameKz,
        serviceGroupId: values.serviceGroupId,
        accuralMethodId: values.accrualMethodId,
        amount: values.amount,
        // Фаза 3: передаём AllowFines и TotalMonths (бэкенд выставит дефолты по группе если null)
        allowFines: values.allowFines,
        totalMonths: values.totalMonths,
        // Ежемесячное подтверждение
        requiresMonthlyConfirmation: values.requiresMonthlyConfirmation
      };

      const mutation = queryClient.getMutationCache().build(
        queryClient,
        getPostApiOsiServicesMutationOptions({
          mutation: {
            onMutate: () => {
              this.isLoading = true;
            },
            onSettled: () => {
              this.isLoading = false;
            }
          }
        })
      );

      const response: ApiResponseDbOsiServiceOSICoreTypes = await mutation.execute({
        data: payload
      });

      // If code is 100, show confirmation dialog
      if (response && response.code === 100) {
        this.forceUpdatePayload = {
          ...payload,
          forceUpdate: true
        };
        this.forceUpdateMessage = response.message ?? 'Требуется подтверждение для обновления';
        this.isForceUpdateConfirmDialogOpen = true;
        return;
      }
      if (response && response.code !== 0) {
        // If code !== 0 and code !== 100, show error
        notifications.error(response.message ?? '');
        return;
      }

      // OSI-805: POST /api/OsiServices возвращает EF-сущность Db.OsiService (ApiResponseDbOsiServiceOSICoreTypes),
      // а НЕ OsiServiceResponse — в ней нет ни serviceGroupCode, ни accuralMethodCode. При этом окно выбора
      // помещений открывается АВТОМАТИЧЕСКИ сразу после создания услуги (openAbonentsTable ниже), и именно оно
      // решает по accuralMethodCode, какую колонку показать: «Проживают» (TARIF_1LIVING) или «Площадь».
      // Без этой достройки председатель, только что создавший услугу с тарифом «с одного проживающего»,
      // увидел бы «Площадь» — то есть самый частый (автоматический) путь открытия окна работал бы неверно.
      // Код берём из списка методов формы: там он приходит с бэка в group.accuralMethods, а id уже выбран
      // пользователем в этой же форме — второго запроса на сервер не требуется.
      const createdService: OsiServiceResponse | null = response.result ?? null;
      const selectedAccrualMethod = this._accrualMethods.find((method) => method.id === values.accrualMethodId);

      // Присваиваем _service напрямую, а не через сеттер service: тело сеттера делает ровно это же
      // (this._service = service), поведение не меняется, но сеттер объявлен как принимающий
      // OsiServiceResponse без null, и ветка «услуга не вернулась» на нём не сходилась по типам.
      this._service = createdService
        ? { ...createdService, accuralMethodCode: selectedAccrualMethod?.code ?? null }
        : null;
      // this.reloadCb?.();
      await this.openAbonentsTable();
      this.closeDialog();
    } catch (e) {
      logger.error(e);
      if (e instanceof Error) {
        notifications.error();
        return;
      }
      notifications.error(e);
    } finally {
      this.isLoading = false;
    }
  };

  openAbonentsTable = async () => {
    if (!this.service) {
      this.reloadCb?.();
      return;
    }

    await this._abonentsTableRef?.current?.openTableManual(this.service);
  };

  setHookForm = (hookForm: any) => {
    this.hookForm = hookForm;
  };
}
